// hook useStaticData
import {useUser} from "@/base/components/auth/UserProvider";
import {ModifyURL} from "@/base/utils/fetch";
import {useNotification} from "@/base/components/NotificationProvider";
import {AllowedPhoneCountry} from "@/base/types/AllowedPhoneCountry";
import {withCache} from "@/base/utils/cache";
import {CartPlans} from "@/base/types/Cart/CartPlans";
import {getCartPlans} from "@/base/utils/CartConfig";
import {useRouter} from "next/router";
import {useEffect, useState} from "react";
import SupportStatus from "@/base/types/SupportStatus";

export const useMoneyRates = (deps: any[] = []) => {
	const [rates, setCurrencies] = useState<PartialRecord<string, number>>({});

	const StaticData = useStaticData();

	useEffect(() => {
		StaticData.getMoneyRates().then(rates => {
			setCurrencies(rates)
		});
	}, [...deps]);

	return [rates];
}

export const useStaticData = () => {
	const UserContext = useUser();

	const [api] = useNotification();
	const router = useRouter();

	const getMoneyRates = async (): Promise<PartialRecord<string, number>> => {
		const o = await withCache(
			"money_rates",
			10 * 60 * 1000,
			() => UserContext.Fetch({
				url: ModifyURL(`/money/rates`),
			}).then((res: {
				data: {
					rates: {
						string: number
					}
				}
			}) => {
				return res?.data?.rates || undefined;
			}).catch(err => {
				api.error({
					message: err.message || err
				});

				return undefined;
			})
		);

		return o || {};
	}

	const getSupportStatus = async (): Promise<SupportStatus> => {
		const o = await withCache(
			`support_status_${router.locale}`,
			5000,
			() => UserContext.Fetch({
				url: ModifyURL(`/support/status`),
			}).then((res: {
				data: SupportStatus
			}) => {
				return res?.data || undefined;
			}).catch(err => {
				api.error({
					message: err.message || err
				});

				return undefined;
			})
		);

		return o || {
			disabled: false,
			message: undefined
		};
	}

	const getAvailableGeoServices = async (): Promise<string[]> => {
		return (await withCache(
			'available_geo_services',
			10 * 60 * 1000,
			() => UserContext.Fetch({
				url: ModifyURL(`/cart/available-geo-services`),
			}).then((res: {
				data: string[]
			}) => {
				if (!res?.data || !Array.isArray(res?.data))
				{
					throw new Error("Invalid geo services");
				}

				return res?.data || [];
			}).catch(err => {
				api.error({
					message: err.message || err
				});

				return [];
			})
		)) || [];
	}

	const getAvailableCountries = async (geoService?: string): Promise<Record<string, Record<string, number>>> => {
		return (await withCache(
			`available_countries_overall${geoService ? `_${geoService}` : ''}`,
			10 * 60 * 1000,
			() => UserContext.Fetch({
				url: ModifyURL(`/cart/available-countries`),
				params: {
					service: geoService
				}
			}).then((res: {
				data: {
					available_countries: Record<string, Record<string, number>>
				}
			}) => {
				return res?.data?.available_countries || undefined
			}).catch(err => {
				api.error({
					message: err.message || err
				});

				return undefined;
			})
		)) || {};
	}

	const getPhoneCountries = async (): Promise<AllowedPhoneCountry[]> => {
		return (await withCache(
			'phone_supported_countries',
			60 * 60 * 1000,
			() => UserContext.Fetch({
				url: ModifyURL(`/phone/supported-countries`),
			}).then((res: {
				data: {
					allowed: AllowedPhoneCountry[]
				}
			}) => {
				if (!res?.data || !Array.isArray(res?.data?.allowed))
				{
					throw new Error("Invalid phone countries");
				}

				return (res?.data?.allowed || []).map((country: any) => country as AllowedPhoneCountry);
			}).catch(err => {
				api.error({
					message: err.message || err
				});

				return undefined;
			})
		)) || [];
	}

	const getCartConfig = async (): Promise<CartPlans> => {
		return (await withCache(
			'cart_config_2',
			5 * 60 * 1000,
			() => getCartPlans(router.locale || 'en').then((res) => {
				if (!res || Object.keys(res).length === 0)
				{
					throw new Error("Invalid cart config");
				}

				return res || {} as CartPlans;
			}).catch(err => {
				api.error({
					message: err.message || err
				});

				return undefined;
			})
		)) || {} as CartPlans;
	}

	return {
		getMoneyRates,
		getPhoneCountries,
		getAvailableCountries,
		getCartConfig,
		getSupportStatus,
		getAvailableGeoServices
	}
}